import { useEffect, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { API } from "@/lib/api";

const isVideo = (ct) => ct && ct.startsWith("video/");
const MIN = 1;
const MAX = 5;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export default function Lightbox({ files, index, onClose, onPrev, onNext }) {
  const current = files && files[index];
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const drag = useRef(null);
  const pinch = useRef(null);
  const overlayRef = useRef(null);

  const reset = useCallback(() => { setScale(1); setTx(0); setTy(0); }, []);
  useEffect(() => { reset(); }, [index, reset]);

  // Body scroll lock + keyboard
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, onPrev, onNext]);

  // Non-passive wheel handler so we can preventDefault and stop page scroll
  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      if (isVideo(current?.content_type)) return;
      const delta = -e.deltaY * 0.0015;
      setScale((s) => {
        const next = clamp(s + s * delta, MIN, MAX);
        if (next === 1) { setTx(0); setTy(0); }
        return next;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [current]);

  if (!current) return null;

  const url = `${API}/files/${current.id}`;
  const video = isVideo(current.content_type);
  const stop = (e) => e.stopPropagation();
  const hasMany = files.length > 1;

  // Mouse drag-pan
  const onMouseDown = (e) => {
    if (video || scale === 1) return;
    e.preventDefault();
    drag.current = { x: e.clientX - tx, y: e.clientY - ty };
  };
  const onMouseMove = (e) => {
    if (!drag.current) return;
    setTx(e.clientX - drag.current.x);
    setTy(e.clientY - drag.current.y);
  };
  const endDrag = () => { drag.current = null; };

  // Touch pinch + pan
  const d = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  const onTouchStart = (e) => {
    if (video) return;
    if (e.touches.length === 2) pinch.current = { d: d(e.touches[0], e.touches[1]), s: scale };
    else if (e.touches.length === 1 && scale > 1) drag.current = { x: e.touches[0].clientX - tx, y: e.touches[0].clientY - ty };
  };
  const onTouchMove = (e) => {
    if (video) return;
    if (e.touches.length === 2 && pinch.current) {
      e.preventDefault();
      const next = clamp(pinch.current.s * (d(e.touches[0], e.touches[1]) / pinch.current.d), MIN, MAX);
      setScale(next);
      if (next === 1) { setTx(0); setTy(0); }
    } else if (e.touches.length === 1 && drag.current) {
      setTx(e.touches[0].clientX - drag.current.x);
      setTy(e.touches[0].clientY - drag.current.y);
    }
  };
  const onTouchEnd = () => { drag.current = null; pinch.current = null; };

  const node = (
    <div
      ref={overlayRef}
      onClick={onClose}
      onMouseMove={onMouseMove}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      role="dialog"
      aria-modal="true"
      data-testid="lightbox-overlay"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0, 0, 0, 0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {/* Close — white circle with black X */}
      <button
        type="button"
        onClick={(e) => { stop(e); onClose(); }}
        data-testid="lightbox-close"
        aria-label="Close"
        style={{
          position: "fixed",
          top: 48, right: 48, zIndex: 10001,
          width: 44, height: 44, borderRadius: "50%",
          background: "#FFFFFF", color: "#000000",
          border: 0, cursor: "pointer",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 14px rgba(0,0,0,0.3)",
        }}
      >
        <X size={20} strokeWidth={2.5} />
      </button>

      {hasMany && (
        <>
          <button
            type="button"
            onClick={(e) => { stop(e); onPrev(); }}
            data-testid="lightbox-prev"
            aria-label="Previous"
            style={{
              position: "fixed", left: 24, top: "50%", transform: "translateY(-50%)",
              zIndex: 10001, width: 44, height: 44, borderRadius: "50%",
              background: "rgba(255,255,255,0.12)", color: "#FFFFFF",
              border: 0, cursor: "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <ChevronLeft size={22} strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={(e) => { stop(e); onNext(); }}
            data-testid="lightbox-next"
            aria-label="Next"
            style={{
              position: "fixed", right: 24, top: "50%", transform: "translateY(-50%)",
              zIndex: 10001, width: 44, height: 44, borderRadius: "50%",
              background: "rgba(255,255,255,0.12)", color: "#FFFFFF",
              border: 0, cursor: "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <ChevronRight size={22} strokeWidth={2} />
          </button>
        </>
      )}

      {video ? (
        <video
          key={current.id}
          onClick={stop}
          data-testid="lightbox-media"
          src={url}
          controls
          autoPlay
          style={{ maxWidth: "90vw", maxHeight: "90vh", display: "block", objectFit: "contain" }}
        />
      ) : (
        <img
          key={current.id}
          onClick={stop}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onDragStart={(e) => e.preventDefault()}
          data-testid="lightbox-media"
          src={url}
          alt=""
          draggable={false}
          style={{
            maxWidth: "90vw", maxHeight: "90vh",
            display: "block", objectFit: "contain",
            transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
            transformOrigin: "center center",
            cursor: scale > 1 ? (drag.current ? "grabbing" : "grab") : "zoom-in",
            transition: drag.current ? "none" : "transform 0.15s ease",
            userSelect: "none",
            willChange: "transform",
          }}
        />
      )}

      {current.caption && (
        <p
          onClick={stop}
          data-testid="lightbox-caption"
          style={{
            position: "fixed", bottom: 32, left: 0, right: 0,
            textAlign: "center", padding: "0 24px",
            color: "#FFFFFF", fontFamily: "DM Sans, sans-serif",
            fontSize: 13, zIndex: 10001, margin: 0,
          }}
        >
          {current.caption}
        </p>
      )}
    </div>
  );

  return createPortal(node, document.body);
}
